import { Button, Dropdown, Space, Table, Input } from 'antd';
import useModal from 'antd/es/modal/useModal';
import { ExportPayrollForm } from './export-payroll-form';
import {
  DeleteOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  useCreateEmployeePayroll,
  useEmployeePayrolls,
  usePayEmployeePayroll,
  useSendConfirmationEmployeePayroll,
  useVoidEmployeePayroll,
} from '../../utils/api-hooks/employee.hook';
import { usePagination } from '../../shared/usePagination';
import {
  EMPLOYEE_PAYROLL_STATUS,
  EMPLOYEE_PAYROLL_STATUS_COLOR,
  EMPLOYEE_PAYROLL_STATUS_LABEL,
} from '../../constants/employee';
import { every, find, keys, map, size, some } from 'lodash';
import moment from 'moment';
import { Key, useRef, useState } from 'react';
import { FormInstance, Popover } from 'antd/lib';
import { FindEmployeePayrollsResultDataItem } from '../../utils/api-client';

const allowSendConfirmation = [
  EMPLOYEE_PAYROLL_STATUS.REVIEWING,
  EMPLOYEE_PAYROLL_STATUS.EMPLOYEE_DISPUTED,
];

export function EmployeePayrolls() {
  const [modal, contextHolder] = useModal();
  const [selectedIds, setSelectedIds] = useState<Key[]>([]);
  const pagination = usePagination();
  const [filter, setFilter] = useState<{
    status?: EMPLOYEE_PAYROLL_STATUS[];
    employeeName?: string; // 新增員工姓名搜尋
  }>({});
  const formRef = useRef<
    FormInstance<{
      month: string;
      employeeIds: string[] | undefined;
    }>
  >(null);
  const {
    data: { data: payrolls = [], total } = {},
    isLoading: isPayrollsLoading,
    isFetching,
  } = useEmployeePayrolls(
    pagination.skip,
    pagination.take,
    filter.status as EMPLOYEE_PAYROLL_STATUS[] | undefined,
    filter.employeeName // 傳遞搜尋參數給 API
  );
  const { mutateAsync: createEmployeePayroll, isLoading: isCreatingPayroll } =
    useCreateEmployeePayroll();
  const { mutateAsync: voidEmployeePayroll, isLoading: isVoidPayrollLoading } =
    useVoidEmployeePayroll();
  const {
    mutateAsync: sendConfirmation,
    isLoading: isSendConfirmationLoading,
  } = useSendConfirmationEmployeePayroll();
  const {
    mutateAsync: payEmployeePayroll,
    isLoading: isPayEmployeePayrollLoading,
  } = usePayEmployeePayroll();

  const isLoading =
    isPayrollsLoading ||
    isFetching ||
    isCreatingPayroll ||
    isVoidPayrollLoading ||
    isSendConfirmationLoading ||
    isPayEmployeePayrollLoading;

  const allowToSendConfirmation =
    size(selectedIds) &&
    every(
      map(selectedIds, (id) => find(payrolls, (p) => p.id === id)?.status),
      (status) =>
        allowSendConfirmation.includes(status as EMPLOYEE_PAYROLL_STATUS)
    );

  const onPayButtonClick = (record: FindEmployeePayrollsResultDataItem) => {
    modal.confirm({
      title: '匯款薪資單',
      content: `確定要將 ${record.displayName} ${moment(record.time)
        .utcOffset(8)
        .format('M月')} (${record.amount.toLocaleString('zh-TW', {
        style: 'currency',
        currency: 'TWD',
        minimumFractionDigits: 0,
      })}) 的薪資單標記為已匯款嗎？`,
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        return payEmployeePayroll({
          ids: [record.id],
        });
      },
    });
  };

  const onExportButtonClick = () => {
    modal.confirm({
      title: '匯出薪資明細',
      content: <ExportPayrollForm formRef={formRef} />,
      okText: '匯出',
      cancelText: '取消',
      icon: null,
      onOk: async () => {
        const values = await formRef.current?.validateFields();

        return createEmployeePayroll({
          time: values!.month,
          employeeIds: values?.employeeIds,
        });
      },
    });
  };

  const onVoidButtonClick = (record: FindEmployeePayrollsResultDataItem) => {
    modal.confirm({
      title: '作廢薪資單',
      content: `確定要作廢 ${record.displayName} ${moment(record.time).utcOffset(8).format('M月')} (${record.amount.toLocaleString(
        'zh-TW',
        {
          style: 'currency',
          currency: 'TWD',
          minimumFractionDigits: 0,
        }
      )}) 的薪資單嗎？`,
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        return voidEmployeePayroll(record.id);
      },
    });
  };

  const onBatchPayButtonClick = async () => {
    modal.confirm({
      title: '批次付款單資單',
      content: `確定要將選中的薪資單批次轉換為已付款嗎？`,
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        return payEmployeePayroll({
          ids: selectedIds as string[],
        }).then(() => {
          setSelectedIds([]);
        });
      },
    });
  };

  const onSendConfirmationButtonClick = async () => {
    if (!allowToSendConfirmation) {
      modal.error({
        title: '無法發送薪資單確認訊息',
        content: '僅能對審核中或有異議的薪資單發送確認訊息',
      });
      return;
    }

    modal.confirm({
      title: '發送薪資單確認訊息',
      content: '確定要發送薪資單確認訊息給選中的薪資單嗎？',
      onOk: async () => {
        return sendConfirmation({
          ids: selectedIds as string[],
        }).then(() => {
          setSelectedIds([]);
        });
      },
    });
  };

  // 新增搜尋處理函數
  const onEmployeeNameSearch = (value: string) => {
    setFilter((prev) => ({
      ...prev,
      employeeName: value.trim() || undefined,
    }));
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {contextHolder}
      <span className="text-2xl font-bold">員工薪資明細</span>

      {/* 新增搜尋區域 */}
      <div className="flex items-center p-4 rounded gap-4 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">員工姓名:</span>
          <Input.Search
            placeholder="搜尋員工姓名"
            allowClear
            style={{ width: 300 }}
            onSearch={onEmployeeNameSearch}
            enterButton={<SearchOutlined />}
          />
        </div>
      </div>

      <Table
        dataSource={payrolls}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="small"
        onChange={(pagination, filters) => {
          setFilter((prev) => ({
            ...prev,
            status: filters.status as EMPLOYEE_PAYROLL_STATUS[],
          }));
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedIds,
          onChange: (selectedRowKeys) => {
            setSelectedIds(selectedRowKeys);
          },
        }}
        pagination={{
          ...pagination,
          total,
        }}
        columns={[
          {
            align: 'center',
            width: 100,
            title: '狀態',
            dataIndex: 'status',
            filters: keys(EMPLOYEE_PAYROLL_STATUS_LABEL).map((status) => ({
              text: EMPLOYEE_PAYROLL_STATUS_LABEL[
                status as EMPLOYEE_PAYROLL_STATUS
              ],
              value: status,
            })),
            render: (status) => {
              return (
                <span
                  className={
                    EMPLOYEE_PAYROLL_STATUS_COLOR[
                      status as EMPLOYEE_PAYROLL_STATUS
                    ]
                  }
                >
                  {
                    EMPLOYEE_PAYROLL_STATUS_LABEL[
                      status as EMPLOYEE_PAYROLL_STATUS
                    ]
                  }
                </span>
              );
            },
          },
          {
            width: 300,
            title: '狀態變更紀錄',
            dataIndex: 'statusHistory',
            render: (v, r) => {
              const { statusHistory = [] } = r;
              return map(statusHistory, (history, index) => (
                <div key={index} className="">
                  {moment(history.time).format('YYYY-MM-DD HH:mm:ss')} -{' '}
                  {
                    EMPLOYEE_PAYROLL_STATUS_LABEL[
                      history.status as EMPLOYEE_PAYROLL_STATUS
                    ]
                  }
                </div>
              ));
            },
          },
          {
            title: '員工姓名',
            dataIndex: 'displayName',
            key: 'employeeName',
          },
          {
            align: 'center',
            title: '薪資月份',
            dataIndex: 'time',
            width: 100,
            render: (time) => {
              return moment(time).utcOffset(8).format('YYYY-MM');
            },
          },
          {
            align: 'center',
            title: '薪資金額',
            dataIndex: 'amount',
            width: 100,
            render: (amount) => {
              return `${amount.toLocaleString('zh-TW', {
                style: 'currency',
                currency: 'TWD',
                minimumFractionDigits: 0,
              })}`;
            },
          },
          {
            align: 'center',
            width: 80,
            title: '薪資單',
            dataIndex: 'pdfUrl',
            render: (pdfUrl) => {
              return (
                <Button
                  type="link"
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  下載
                </Button>
              );
            },
          },
          {
            align: 'center',
            title: '操作',
            width: 50,
            render: (text: any, record) => {
              return (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'pay',
                        label: '轉為已匯款',
                        disabled:
                          record.status !==
                          EMPLOYEE_PAYROLL_STATUS.READY_FOR_PAY,
                        onClick: () => {
                          onPayButtonClick(record);
                        },
                      },
                      {
                        key: 'divider',
                        type: 'divider',
                      },
                      {
                        key: 'void',
                        label: '作廢',
                        danger: true,
                        disabled:
                          record.status === EMPLOYEE_PAYROLL_STATUS.VOIDED ||
                          record.status === EMPLOYEE_PAYROLL_STATUS.PAID,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                          onVoidButtonClick(record);
                        },
                      },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} shape="circle" size="small" />
                </Dropdown>
              );
            },
          },
        ]}
        title={() => (
          <Space size="small">
            <Button type="primary" onClick={onExportButtonClick}>
              匯出薪資明細
            </Button>
            <Popover
              open={!allowToSendConfirmation && size(selectedIds) > 0}
              content="僅能對審核中或有異議的薪資單發送確認訊息"
            >
              <Button
                type="primary"
                disabled={!allowToSendConfirmation}
                onClick={onSendConfirmationButtonClick}
              >
                發送薪資單確認訊息
              </Button>
            </Popover>
            <Popover
              content={'請確認選中的薪資單狀態為「待匯款」'}
              placement="bottom"
              open={
                size(selectedIds) > 0 &&
                some(
                  selectedIds,
                  (id) =>
                    find(payrolls, (p) => p.id === id)?.status !==
                    EMPLOYEE_PAYROLL_STATUS.READY_FOR_PAY
                )
              }
            >
              <Button
                type="primary"
                onClick={onBatchPayButtonClick}
                disabled={
                  size(selectedIds) === 0 ||
                  some(
                    selectedIds,
                    (id) =>
                      find(payrolls, (p) => p.id === id)?.status !==
                      EMPLOYEE_PAYROLL_STATUS.READY_FOR_PAY
                  )
                }
              >
                將薪資單轉為已匯款
              </Button>
            </Popover>
          </Space>
        )}
      />
    </Space>
  );
}
